// SAL6010_R01 비즈니스 로직 훅
// - 조회/엑셀/인쇄/저장/삭제/수주처리 등 액션을 캡슐화하여 컨테이너에서 사용합니다.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { downloadSettingExcel } from "src/utils/blob/blobUtil";

import { defaultSAL6010_R01Filter, SAL6010_R01Filter, useSAL6010_R01Store } from 'src/containers/mes/SAL/SAL6010_R01/Store_SAL6010_R01';
import useFetchActionbarAuth from 'src/hooks/useFetchActionbarAuth';
import { Tab } from 'src/store/useUiuxStore';
import { useLoadingSpinnerStore } from 'src/store/util/loadingSpinnerUtil';
import { showError, showSuccess, ShowSuccessFetch, showWarning } from 'src/utils/toast/toastUtil';
import useUiuxStore from 'src/store/useUiuxStore';
import { formatDate, validateString } from 'src/utils/string/stringUtil';

import { deleteSAL6010_R01, downloadExcel_SAL6010_R01, getSAL6010_R01AllCount, getSAL6010_R01DetailList, getSAL6010_R01List, getSAL6010_R01One, getPdfBlob_SAL6010_R01, patchSAL6010_R01, postSAL6010_R01 } from "./Api_SAL6010_R01";
import { defaultSAL6010_R01DetailDto, defaultSAL6010_R01HeaderDto, SAL6010_R01DetailDto, SAL6010_R01Dto, SAL6010_R01HeaderDto } from './Dto_SAL6010_R01';
import { filterDeletedItems } from 'src/utils/calculation/calculationUtils';
import { AgGridReact } from 'ag-grid-react';
import { fetchDataSetHandler, fetchFilterConfig, gridRefSelectedRowHandler } from 'src/utils/grid/GridHandlerContent';
 
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
import { getMenuComponent } from 'src/utils/menu/menuUtil';
import { validateCustomCd, validateEmployeeCd } from 'src/utils/grid/GridValidateContent';
import { useCommonCodeValidate } from 'src/utils/commonCode/CommonCodeValidateContext';
import {
	buildCommonCodeRequirements,
	SAL6010_R01_HEADER_ROW_VALIDATE,
} from 'src/utils/commonCode/commonCodeRequirementConfig';
import { useQzPrint } from 'src/hooks/useQzPrint';
import qz from 'qz-tray';

// 컨테이너로부터 필요한 ref/상태/콜백을 전달받습니다.
interface Props {
	gridRef: React.RefObject<AgGridReact<SAL6010_R01HeaderDto> | null>;
	detailGridRef: React.RefObject<AgGridReact<SAL6010_R01DetailDto> | null>;

	tabList: Tab[] | undefined;
	setMessage: (message: string) => void;

	containerRef: React.RefObject<HTMLDivElement | null>;
	isResizing: React.MutableRefObject<boolean>;
	
}

export const Content_SAL6010_R01 = (props: Props) => {

	const {
		gridRef, detailGridRef,
		tabList, setMessage,
		containerRef,
		isResizing,
	} = props;

	const { moneyCdList, vatTypeList, compCdList, customCdList, employeeCdList, itemCdMap } = useCommonCodeStore();
	const { printPdfFromBlob } = useQzPrint();

	const {
		SAL6010_R01Filter, setSAL6010_R01Filter,
		SAL6010_R01PastFilter, setSAL6010_R01PastFilter,
		SAL6010_R01List, setSAL6010_R01List,
		editSAL6010_R01, setEditSAL6010_R01,
		selectedSAL6010_R01, setSelectedSAL6010_R01, 
		deletedSAL6010_R01DetailList, setDeletedSAL6010_R01DetailList, addDeletedSAL6010_R01DetailList, removeDeletedSAL6010_R01DetailList, 
		selectedSAL6010_R01DetailList, setSelectedSAL6010_R01DetailList, addSelectedSAL6010_R01DetailList, removeSelectedSAL6010_R01DetailList,
		SAL6010_R01DetailList, setSAL6010_R01DetailList, insertSAL6010_R01DetailList,
		originalSAL6010_R01DetailList, setOriginalSAL6010_R01DetailList,
		allCount, setAllCount,
		detailFetchTrigger, setDetailFetchTrigger,
		initData,
		setScrollTop, setDetailScrollTop,
	} = useSAL6010_R01Store();
	const { setLoadingSpinner } = useLoadingSpinnerStore();
	const { validateCommonCodeLists } = useCommonCodeValidate();
	const { SAL6020_SAL6010NavigationData, setSAL6020_SAL6010NavigationData, setSAL6020_SAL6010OrderNavigationData, addTabList, setActiveTab } = useUiuxStore();

	// 네비게이션 조회 트리거
	const [navigationTrigger, setnavigationTrigger] = useState(false);

	useFetchActionbarAuth("SAL6010_R01");

	// 메시지 타이머 유틸
	const msgTimer = useRef<number | null>(null);
	// 메시지를 잠시 띄웠다가 자동으로 숨깁니다.
	const setTempMessage = useCallback((msg: string, ms = 5000) => {
		setMessage(msg);
		if (msgTimer.current) window.clearTimeout(msgTimer.current);
		msgTimer.current = window.setTimeout(() => setMessage(''), ms);
	}, [setMessage]);

	useEffect(() => () => { if (msgTimer.current) window.clearTimeout(msgTimer.current); }, []);

	// =============================================
	// 출력: QZ Tray 연결 시 프린터 출력, 미연결 시 새 탭
	// =============================================
	const handlePrintClick = useCallback(async () => {
		if (!selectedSAL6010_R01?.salesEstimateNo) {
			showWarning("출력할 견적서를 선택하세요.");
			return;
		}

		setLoadingSpinner(true);
		try {
			const blob = await getPdfBlob_SAL6010_R01(selectedSAL6010_R01.salesEstimateNo);

			if (qz.websocket.isActive()) {
				// QZ Tray 연결됨 → 프린터로 직접 출력 시도
				const ok = await printPdfFromBlob(blob, "report1");
				if (ok) {
					showSuccess("PDF 출력을 완료하였습니다.");
				} else {
					// QZ로 실패 시 새 탭에서 PDF 미리보기만 제공합니다.
					const fileURL = URL.createObjectURL(blob);
					window.open(fileURL, "_blank");
					showWarning("PDF를 새 탭에서 열었습니다.");
				}
			} else {
				// QZ Tray 미연결 → 새 탭에서 미리보기
				const fileURL = URL.createObjectURL(blob);
				window.open(fileURL, "_blank");
				showSuccess("PDF를 새 탭에서 열었습니다.");
			}
		} catch (error) {
			console.error("Print error:", error);
			showError("PDF 출력 중 오류가 발생했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [selectedSAL6010_R01, printPdfFromBlob, setLoadingSpinner]);

	const handleExcelClick = useCallback(async () => {
		if (!selectedSAL6010_R01?.salesEstimateNo) {
			showWarning('견적번호가 선택되지 않았습니다.');
			return;
		}
		setLoadingSpinner(true);
		try {
			const response = await downloadExcel_SAL6010_R01(selectedSAL6010_R01.salesEstimateNo);
			downloadSettingExcel({ responseData: response.data.data, fileName: '견적서 출력-excel.xlsx' });
		} catch (error) {
			console.error('Excel download error:', error);
			showError('엑셀 다운로드 중 오류가 발생했습니다.');
			throw error;
		} finally {
			setLoadingSpinner(false);
		}
	}, [selectedSAL6010_R01, setLoadingSpinner])

	// 화면 필터를 API 파라미터 포맷으로 변환
	const toApiParams = (filter: SAL6010_R01Filter) => ({
		'salEstimateHeaderDto.compCd': filter.compCd || undefined,
		'salEstimateHeaderDto.customCd': filter.custInput || undefined,
		'salEstimateHeaderDto.salesEstimateNo': filter.salesEstimateNo || undefined,
		startDate: filter.dateRange?.from ? formatDate(filter.dateRange.from) : undefined,
		endDate: filter.dateRange?.to ? formatDate(filter.dateRange.to) : undefined,
	});

	// 전체 건수 재조회 (페이징/무한스크롤 UX 개선)
	const renewalAllCount = useCallback(async () => {
		const response = await getSAL6010_R01AllCount(toApiParams(SAL6010_R01Filter) as any);
		setAllCount(response.data.data);
		return response.data.data;
	}, [SAL6010_R01Filter, setAllCount]);

	// 빈 상태 처리: 선택 해제 및 기본행 추가 등 UI 일관성 유지
	const applyEmptyState = useCallback(() => {
		showWarning('조회된 데이터가 없습니다.');
		setSelectedSAL6010_R01(undefined);
		setSAL6010_R01List([]);
		setSAL6010_R01DetailList([]);
		setDeletedSAL6010_R01DetailList([]);
		setSelectedSAL6010_R01DetailList([]);
		setOriginalSAL6010_R01DetailList([]);
	}, [
		setSelectedSAL6010_R01, setSAL6010_R01List, setSAL6010_R01DetailList,
		setDeletedSAL6010_R01DetailList, setSelectedSAL6010_R01DetailList, setOriginalSAL6010_R01DetailList
	]);

	const lastFetchId = useRef(0);

	// 기본 조회 (필터 비교를 통한 증분/전체 조회 분기 포함)
	const fetchData = useCallback(async () => {
		const fetchId = ++lastFetchId.current;
		setScrollTop(null);
		setDetailScrollTop(null);
		setLoadingSpinner(true);
		try {

			const responseData = (await getSAL6010_R01List(
				'',
				SAL6010_R01Filter,
				SAL6010_R01List.length ?? 0
			));


			let newList = [...responseData.data.data ] as SAL6010_R01HeaderDto[];
			setSAL6010_R01List(newList);


			const processAllCount = await renewalAllCount();
			if (newList && newList.length > 0) {
				gridRefSelectedRowHandler(
					gridRef,
					setSelectedSAL6010_R01,
					() => true,
				);
				setSAL6010_R01PastFilter(SAL6010_R01Filter);
				if (fetchId !== lastFetchId.current) return responseData;
				ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총${newList.length}/${processAllCount}건)`);
				setTimeout(() => setMessage(""), 5000);
			}else {
				applyEmptyState();
				handleCreateClick();
			}
			if (fetchId !== lastFetchId.current) return responseData;
			return responseData;
	} catch (error) {
		console.error(error);
		showError('조회 중 오류가 발생했습니다.');
		throw error;
	} finally {
		if (fetchId === lastFetchId.current) setLoadingSpinner(false);
	}
	}, [SAL6010_R01Filter, SAL6010_R01List, setSAL6010_R01List, setSAL6010_R01PastFilter, setSelectedSAL6010_R01, setLoadingSpinner, renewalAllCount, applyEmptyState, setScrollTop, setDetailScrollTop])

	const defaultSAL6010_R01HeaderRow = useCallback(() => {
		const commonResult = validateCommonCodeLists(
			buildCommonCodeRequirements(SAL6010_R01_HEADER_ROW_VALIDATE, {
				compCdList,
				moneyCdList,
				vatTypeList,
			}),
		);
		if (!commonResult.valid) {
			return { ...defaultSAL6010_R01HeaderDto };
		}

		return {
			...defaultSAL6010_R01HeaderDto,
			compCd: '1000',
			vatType: vatTypeList[0].minorCd,
			moneyCd: moneyCdList[0].minorCd,
			inserted: true,
		};
	}, [defaultSAL6010_R01HeaderDto, compCdList, vatTypeList, moneyCdList, validateCommonCodeLists]);

	const defaultSAL6010_R01DetailRow = useCallback(() => {
		return {
			...defaultSAL6010_R01DetailDto,
			inserted: true,
		}
	}, [defaultSAL6010_R01DetailDto]);

	const fetchOne = useCallback(async () => {
		if (!selectedSAL6010_R01) return;
		setLoadingSpinner(true);
		try {
			const response = await getSAL6010_R01One(selectedSAL6010_R01.salesEstimateNo);
			const header = response.data.data;
			const resolvedEmpNo = header.empNo || header.estimateEmpNo || '';
			const resolvedEmpNm = header.empNm || employeeCdList.find(emp => emp.empNo === resolvedEmpNo)?.empNm || '';
			setEditSAL6010_R01({
				...defaultSAL6010_R01HeaderRow,
				...header,
				empNo: resolvedEmpNo,
				empNm: resolvedEmpNm,
				estimateEmpNo: header.estimateEmpNo || resolvedEmpNo,
			});
		} catch (error) {
			console.error('조회 중 오류 발생:', error);
			showError('조회 중 오류가 발생했습니다.');
		}
		setLoadingSpinner(false);
	}, [selectedSAL6010_R01, setEditSAL6010_R01, setLoadingSpinner, defaultSAL6010_R01HeaderRow, employeeCdList])

	// 특정 필터로 조회 (타 화면 네비게이션 연동 등)
	// const fetchDataWithFilter = useCallback(async (customFilter: Partial<typeof SAL6010_R01Filter>) => {
	// 	const fetchId = ++lastFetchId.current;
	// 	setLoadingSpinner(true);
	// 	try {

	// 		const responseData = (await getSAL6010_R01List(
	// 			'',
	// 			customFilter as any,
	// 			SAL6010_R01List.length ?? 0
	// 		));

	// 		let newList = [...responseData.data.data ] as SAL6010_R01HeaderDto[];
	// 		setSAL6010_R01List(newList);

	// 		const processAllCount = await renewalAllCount();
	// 		if (newList && newList.length > 0) {
	// 			gridRefSelectedRowHandler(
	// 				gridRef,
	// 				setSelectedSAL6010_R01,
	// 				() => true,
	// 			);
	// 			setSAL6010_R01PastFilter(customFilter as any);
	// 			if (fetchId !== lastFetchId.current) return responseData;
	// 			ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총${newList.length}/${processAllCount}건)`);
	// 		}else {
	// 			applyEmptyState();
	// 		}
	// 		if (fetchId !== lastFetchId.current) return responseData;
	// 		return responseData;
	// 	} catch (error) {
	// 		console.error(error);
	// 		showError('조회 중 오류가 발생했습니다.');
	// 		throw error;
	// 	} finally {
	// 		if (fetchId === lastFetchId.current) setLoadingSpinner(false);
	// 	}
	// }, [SAL6010_R01List, setSAL6010_R01List, setSAL6010_R01PastFilter, setSelectedSAL6010_R01, setLoadingSpinner, renewalAllCount, applyEmptyState])
	
	// 신규 작성 초기화 (기본 코드 셋팅 및 상세 1행 확보)
	const initContainerData = useCallback(() => {
		initData();
		removeDeletedSAL6010_R01DetailList();
		removeSelectedSAL6010_R01DetailList();
		setEditSAL6010_R01({
			...defaultSAL6010_R01HeaderRow(),
			compCd: '1000',
			vatType: vatTypeList[0] ? vatTypeList[0].minorCd : '',
			moneyCd: moneyCdList[0] ? moneyCdList[0].minorCd : '',
			salesEstimateNo: '', // 명시적으로 문자로 정정
		})
	}, [compCdList, vatTypeList, moneyCdList, defaultSAL6010_R01HeaderRow])

	// 상세 삭제: 선택 삭제/표시 삭제 토글 처리
	const handleDeleteClick = useCallback(() => {

		if (deletedSAL6010_R01DetailList.length > 0) {
			removeDeletedSAL6010_R01DetailList(deletedSAL6010_R01DetailList);
			removeSelectedSAL6010_R01DetailList();
			setSAL6010_R01DetailList([...SAL6010_R01DetailList]);
		} else if (selectedSAL6010_R01DetailList.length > 0) {
			addDeletedSAL6010_R01DetailList(selectedSAL6010_R01DetailList);
			removeSelectedSAL6010_R01DetailList(selectedSAL6010_R01DetailList);
		}
	}, [selectedSAL6010_R01DetailList, 
		deletedSAL6010_R01DetailList,
		addDeletedSAL6010_R01DetailList,
		removeDeletedSAL6010_R01DetailList,
		removeSelectedSAL6010_R01DetailList,
		setSAL6010_R01DetailList,
		SAL6010_R01DetailList]);
	
	// 신규 버튼: 헤더/디테일 초기화 후 입력 모드 전환
	const handleCreateClick = useCallback(() => {
		// 목록 모드 변경: 택제, 집이 초기화 되었을 때 목록 초기화
		setSelectedSAL6010_R01(undefined);
		setEditSAL6010_R01({ ...defaultSAL6010_R01HeaderRow(), });
		setSAL6010_R01DetailList([{...defaultSAL6010_R01DetailRow(), inserted: true}]);
		setDeletedSAL6010_R01DetailList([]);
		setSelectedSAL6010_R01DetailList([]);
		setOriginalSAL6010_R01DetailList([]);
		setDetailScrollTop(null);
	}, [
		compCdList, vatTypeList, moneyCdList,
		setSelectedSAL6010_R01, setEditSAL6010_R01,
		setSAL6010_R01DetailList, setDeletedSAL6010_R01DetailList,
		setSelectedSAL6010_R01DetailList, setOriginalSAL6010_R01DetailList, defaultSAL6010_R01HeaderRow, defaultSAL6010_R01DetailRow,
		setDetailScrollTop,
	])
	
	// 저장 전 필수값 검증 및 품목 중복 검사
	const validateInsertData = (header: SAL6010_R01HeaderDto, detailList: SAL6010_R01DetailDto[]) => {
	
		if (!validateString(header.customCd)) {
			showWarning('거래처를 입력해주세요.'); return false;
		}
		if (!validateCustomCd(header.customCd, customCdList)) {
			showWarning('거래처가 존재하지 않습니다.(입력칸을 더블클릭하거나 엔터키로 세부사항을 열어서 지정해주세요)'); return false;
		}
		if (!validateString(header.empNo)) {
			showWarning('담당자를 선택해주세요.'); return false;
		}
		if (!validateEmployeeCd(header.empNo, employeeCdList)) {
			showWarning('담당자가 존재하지 않습니다.(입력칸을 더블클릭하거나 엔터키로 세부사항을 열어서 지정해주세요)'); return false;
		}

		const activeDetailList = filterDeletedItems(detailList, deletedSAL6010_R01DetailList);
	

		for (const detail of activeDetailList) {
			if (!validateString(detail.itemCd)) {
				showWarning('품목코드를 입력 해주세요.'); return false;
			}
			if (detail.itemQty === 0) {
				showWarning('수량을 입력 해주세요.'); return false;
			}

			// 중복 품목코드 검증 (자기 자신 정확히 제외)
			const otherDetailList = activeDetailList.filter(d =>
				(detail.valueIndex != null && d.valueIndex !== detail.valueIndex) ||
				(detail.valueIndex == null && d !== detail)
			);
			if (otherDetailList.some(d => d.itemCd === detail.itemCd)) {
				showWarning(`중복 품목코드가 있습니다.\n품목코드: ${detail.itemCd}`);
				return false;
			}
			if(!itemCdMap.has(detail.itemCd)) {
				showWarning('품목코드가 해당 MES의 품목정보에 존재하지 않습니다.\n품목코드: ' + detail.itemCd); return false;
			}
		}

		return true;
	}

	// 저장 성공 데이터는 신규/수정 플래그를 명시적으로 초기화하여 목록 표시 상태를 안정화합니다.
	const normalizeSavedHeader = useCallback((header: SAL6010_R01HeaderDto) => {
		return {
			...defaultSAL6010_R01HeaderDto,
			...header,
			inserted: false,
			isModified: false,
		} as SAL6010_R01HeaderDto;
	}, []);
	
	// 저장: 신규(post)/수정(patch)/삭제(delete) 분기 처리 후 상태 동기화
	const handleSaveClick = useCallback(async () => {
		if (!editSAL6010_R01) {
			showWarning('견적서 정보가 없습니다.');
			return;
		}

		if (!validateInsertData(editSAL6010_R01, SAL6010_R01DetailList)) {
			return;
		}
		
		const activeDetailList = filterDeletedItems(SAL6010_R01DetailList, deletedSAL6010_R01DetailList);
		
		let processName = '';
		let newDetailList: SAL6010_R01DetailDto[] = [];
		let newHeaderList: SAL6010_R01HeaderDto[] = [];
		let responseData: SAL6010_R01Dto | null = null;

		try {
			if (selectedSAL6010_R01) {
				newDetailList = activeDetailList;

				if (newDetailList.length === 0) {
					processName = 'delete';
					if (!selectedSAL6010_R01?.salesEstimateNo) {
						showError('삭제할 견적서 번호가 없습니다.');
						return;
					}
					
					const deleteResponse = await deleteSAL6010_R01(selectedSAL6010_R01.salesEstimateNo);

					if (deleteResponse?.data?.successFlag) {
						newHeaderList = SAL6010_R01List.filter(value => 
							value.salesEstimateNo !== selectedSAL6010_R01.salesEstimateNo
						);
					} else {
						showError('견적서 삭제에 실패했습니다.');
						return;
					}
				} else {
					processName = 'patch';
					const patchResponse = await patchSAL6010_R01({
						salEstimateHeaderDto: editSAL6010_R01,
						salEstimateDetailDtoList: newDetailList
					});

					if (patchResponse?.data?.successFlag) {
						responseData = patchResponse.data.data?.[0];
						// payload가 비어도 성공으로 간주하고 현재 편집값으로 폴백
						const effectiveHeader = responseData?.salEstimateHeaderDto ?? editSAL6010_R01;
						const normalizedHeader = normalizeSavedHeader(effectiveHeader);
						const effectiveDetail = responseData?.salEstimateDetailDtoList ?? newDetailList;

						newHeaderList = SAL6010_R01List.map(value => {
							if (value.salesEstimateNo === normalizedHeader.salesEstimateNo) {
								return normalizedHeader;
							}
							return value;
						});

						if (!responseData) {
							responseData = {
								salEstimateHeaderDto: normalizedHeader,
								salEstimateDetailDtoList: effectiveDetail
							} as SAL6010_R01Dto;
						} else {
							responseData = {
								...responseData,
								salEstimateHeaderDto: normalizedHeader,
							};
						}
					} else {
						const serverMsg = (patchResponse as any)?.data?.message || (patchResponse as any)?.data?.error || (patchResponse as any)?.data?.msg;
						showError(`견적서 수정에 실패했습니다.${serverMsg ? ' - ' + serverMsg : ''}`);
						return;
					}
				}
			} else {
				processName = 'post';
				const postResponse = await postSAL6010_R01({
					salEstimateHeaderDto: editSAL6010_R01,
					salEstimateDetailDtoList: SAL6010_R01DetailList
				});

				if (postResponse?.data?.successFlag && postResponse.data.data?.[0]) {
					responseData = postResponse.data.data[0];
					
					// 응답 데이터 유효성 검사
					if (!responseData?.salEstimateHeaderDto) {
						showError('생성된 견적서 데이터가 올바르지 않습니다.');
						return;
					}
					const normalizedHeader = normalizeSavedHeader(responseData.salEstimateHeaderDto);
					responseData = {
						...responseData,
						salEstimateHeaderDto: normalizedHeader,
					};

					newHeaderList = [
						...SAL6010_R01List,
						normalizedHeader,
					] as SAL6010_R01HeaderDto[];
				} else {
					const serverMsg = (postResponse as any)?.data?.message || (postResponse as any)?.data?.error || (postResponse as any)?.data?.msg;
					showError(`견적서 생성에 실패했습니다.${serverMsg ? ' - ' + serverMsg : ''}`);
					return;
				}
			}

			// 성공 처리
			if (processName === 'delete') {
				setSAL6010_R01List(newHeaderList);
				setSelectedSAL6010_R01(undefined);
				setSAL6010_R01DetailList([]);
				handleCreateClick();
				showSuccess('견적서가 삭제되었습니다.');
		} else if (processName === 'patch' || processName === 'post') {
			//  기본값을 사용하여 서버 응답 데이터에 클라이언트 상태 플래그 초기화
			const cleanDetailList = (responseData!.salEstimateDetailDtoList || []).map(item => ({
				...defaultSAL6010_R01DetailDto,
				...item,
				// 기본값에서 inserted: false, isModified: false가 자동으로 적용됨
			}));
			
			setSAL6010_R01List(newHeaderList);
			setSAL6010_R01DetailList(cleanDetailList);
			setSelectedSAL6010_R01(responseData!.salEstimateHeaderDto);
			setOriginalSAL6010_R01DetailList(cleanDetailList);
			
			if (processName === 'patch') {
				showSuccess('견적서가 수정되었습니다.');
			} else {
				showSuccess('견적서가 생성되었습니다.');
			}
		}

			// 상태 초기화
			setDeletedSAL6010_R01DetailList([]);
			setSelectedSAL6010_R01DetailList([]);
			setTempMessage('', 0);

		} catch (error: any) {
			console.error('견적서 저장 중 오류:', error);
			const serverMsg = error?.response?.data?.message || error?.response?.data?.error || error?.message;
			showError(`견적서 저장 중 오류가 발생했습니다.${serverMsg ? ' - ' + serverMsg : ''}`);
		}
	
	}, [
		editSAL6010_R01, 
		selectedSAL6010_R01, 
		SAL6010_R01DetailList, 
		deletedSAL6010_R01DetailList, 
		SAL6010_R01List, 
		setSAL6010_R01List,
		handleCreateClick,
		normalizeSavedHeader
	])

	const fetchDetailList = useCallback(async (salesEstimateNo: string) => {
		setDetailScrollTop(null);
		try {
			const response = await getSAL6010_R01DetailList(salesEstimateNo);
			if (response.data.successFlag && response.data.data) {
				const responseData = response.data.data;
				if(responseData && responseData.length > 0){
					// SAL1010과 동일한 패턴: 단순한 데이터 매핑 (서버 데이터는 자동으로 상태 초기화됨)
					const processedData = responseData.map((value, idx) => {
						const qty = Number(value.estimateQty ?? value.itemQty ?? 0);
						const price = Number(value.estimatePrice ?? value.itemPrice ?? 0);
						const dc = Number(value.dcAmt ?? 0);
						return {
							...value,
							valueIndex: value.valueIndex ?? idx + 1,
							orderIndex: value.orderIndex ?? idx + 1,
							orderIndexSeq: value.orderIndexSeq ?? 0,
							estimatePrice: value.estimatePrice ?? value.itemPrice ?? 0,
							estimateQty: value.estimateQty ?? value.itemQty ?? 0,
							estimateUnit: value.estimateUnit ?? value.unit ?? '',
							itemAmt: value.itemAmt ?? (qty * price - dc),
							itemCost: value.itemPrice,
							id: value.id
						} as SAL6010_R01DetailDto;
					});
					setSAL6010_R01DetailList(processedData);
					setOriginalSAL6010_R01DetailList(processedData);
					setTempMessage(`${selectedSAL6010_R01?.customNm}의 상세내역 ${responseData.length}건을 조회하였습니다.`);
				}
				// else {
				// 	setSAL6010_R01DetailList([]);
				// 	insertSAL6010_R01DetailList([{ ...defaultSAL6010_R01DetailRow }]);
				// 	setTempMessage("상세내역이 없습니다. 새로 추가할 수 있습니다.", 3000);
				// }
			} else {
				showError('상세내역 조회에 실패했습니다.');
				setSAL6010_R01DetailList([]);
				insertSAL6010_R01DetailList([{ ...defaultSAL6010_R01DetailRow() }]);
			}
		} catch (error) {
			console.error('상세내역 조회 오류:', error);
			showError('상세내역 조회 중 오류가 발생했습니다.');
			setSAL6010_R01DetailList([]);
			insertSAL6010_R01DetailList([{ ...defaultSAL6010_R01DetailRow() }]);
		}
	}, [
		selectedSAL6010_R01?.customNm,
		setTempMessage,
		setSAL6010_R01DetailList,
		setOriginalSAL6010_R01DetailList,
		insertSAL6010_R01DetailList,
		defaultSAL6010_R01DetailRow,
		setDetailScrollTop,
	]);

	const fetchOneProcess = useCallback(async (salesEstimateNo: string) => {
		await fetchOne();
		await fetchDetailList(salesEstimateNo);
	}, [fetchOne, fetchDetailList]);

	// 견적서 상세조회 및 네비게이션 조회 처리 통합
	useEffect(() => {
		const processDetail = async () => {
			if (!detailFetchTrigger) {
				if (!navigationTrigger) return;

				try {
					await fetchData(); // Table(목록) 조회
				} catch (error) {
					console.error('❌ 자동 조회 실패:', error);
				} finally {
					setnavigationTrigger(false);
				}
				return;
			}

			(detailGridRef.current as any)?.clearRange?.();

			if (!selectedSAL6010_R01?.salesEstimateNo) {
				setDetailFetchTrigger(false);
				return;
			}

			await fetchOneProcess(selectedSAL6010_R01.salesEstimateNo);
			setDetailFetchTrigger(false);
		};

		processDetail();
	}, [
		detailFetchTrigger,
		navigationTrigger,
		selectedSAL6010_R01,
		detailGridRef,
		fetchData,
		fetchOneProcess,
		setDetailFetchTrigger
	])
	
	// 견적 → 수주 처리: SAL1010_R01로 네비게이션 데이터 전달 후 탭 오픈
	const handleOrderProcessClick = useCallback(async () => {
		if (!selectedSAL6010_R01) {
			showWarning("수주 처리할 견적서를 선택해주세요.");
			return;
		}

		if (!editSAL6010_R01) {
			showWarning("견적서 정보가 없습니다.");
			return;
		}

		// 삭제되지 않은 상세 데이터만 필터링
		const activeDetailList = filterDeletedItems(SAL6010_R01DetailList, deletedSAL6010_R01DetailList);
		
		if (activeDetailList.length === 0) {
			showWarning("수주 처리할 상세 항목이 없습니다.");
			return;
		}

		try {
			// 견적 데이터를 수주 데이터 형식으로 변환
			const orderNavigationData = {
				// 견적서 헤더 정보
				salesEstimateNo: selectedSAL6010_R01.salesEstimateNo,
				compCd: editSAL6010_R01.compCd,
				customCd: editSAL6010_R01.customCd,
				customNm: editSAL6010_R01.customNm,
				estimateDt: editSAL6010_R01.estimateDt || '',
				vatType: editSAL6010_R01.vatType,
				moneyCd: editSAL6010_R01.moneyCd,
				telNo: editSAL6010_R01.telNo,
				faxNo: editSAL6010_R01.faxNo,
				email: editSAL6010_R01.email,
				post: editSAL6010_R01.post,
				address: editSAL6010_R01.address,
				detailAddress: editSAL6010_R01.detailAddress,
				empNo: editSAL6010_R01.empNo,
				empNm: editSAL6010_R01.empNm,
				remark: editSAL6010_R01.remark,
				
				// 견적서 상세 정보를 수주 상세 형식으로 변환
				detailList: activeDetailList.map(detail => ({
					itemCd: detail.itemCd,
					itemNm: detail.itemNm,
					itemSpec: detail.itemSpec,
					estimateUnit: detail.estimateUnit || detail.unit,
					estimateQty: detail.estimateQty || detail.itemQty || 1,
					estimatePrice: detail.estimatePrice || detail.itemPrice || 0,
					dcAmt: detail.dcAmt || 0,
					itemAmt: detail.itemAmt || 0,
					remark: detail.remark || ''
				}))
			};

			// UiuxStore에 수주 네비게이션 데이터 설정
			setSAL6020_SAL6010OrderNavigationData(orderNavigationData);

			// SAL1010_R01 탭 열기
			const sal1010Component = getMenuComponent('SAL1010_R01');
			if (sal1010Component) {
				const newTab = {
					key: 'SAL1010_R01',
					title: '수주정보등록',
					component: sal1010Component,
					fixed: false
				};
				
				// 탭 추가 및 활성화
				addTabList(newTab);
				setActiveTab(newTab);
				
				showSuccess("견적서 데이터가 수주 등록 화면으로 전달되었습니다.");
			} else {
				showError("수주정보등록 화면을 찾을 수 없습니다.");
			}

		} catch (error) {
			console.error('수주 처리 중 오류:', error);
			showError("수주 처리 중 오류가 발생했습니다.");
		}
	}, [selectedSAL6010_R01, editSAL6010_R01, SAL6010_R01DetailList, deletedSAL6010_R01DetailList, setSAL6020_SAL6010OrderNavigationData, addTabList, setActiveTab]);

	// SAL6010에서 전달받은 데이터를 저장할 ref
	type EstimateNav = { salesEstimateNo: string } | null;
	const navigationDataRef = useRef<EstimateNav>(null);

	// SAL6010에서 전달받은 데이터로 자동 조회
	useEffect(() => {
		if (SAL6020_SAL6010NavigationData) {
			
			// 네비게이션 데이터를 ref에 저장
			navigationDataRef.current = SAL6020_SAL6010NavigationData;
			
			// 견적번호로만 검색하도록 필터 설정
			const newFilter = {
				compCd: '',
				custInput: '',
				salesEstimateNo: SAL6020_SAL6010NavigationData.salesEstimateNo || '',
				dateRange: undefined
			};
			
			// 필터 설정
			setSAL6010_R01Filter(newFilter);
			
			// 트리거 활성화
			setnavigationTrigger(true);
			
			// 네비게이션 데이터 초기화 (한 번만 사용)
			setSAL6020_SAL6010NavigationData(null);
		}
	}, [SAL6020_SAL6010NavigationData, setSAL6020_SAL6010NavigationData, setSAL6010_R01Filter]);
	
	// 조회 완료 후 견적서 자동 선택
	useEffect(() => {
		if (navigationDataRef.current && SAL6010_R01List.length > 0) {
			const targetEstimate = SAL6010_R01List.find(item => 
				item.salesEstimateNo === (navigationDataRef.current as any).salesEstimateNo
			);
			if (targetEstimate) {
				setSelectedSAL6010_R01(targetEstimate);
				navigationDataRef.current = null; // 한 번만 실행되도록 초기화
			} else {
				console.log('⚠️ 해당 견적서를 찾을 수 없음:', navigationDataRef.current.salesEstimateNo);
			}
		}
	}, [SAL6010_R01List, setSelectedSAL6010_R01]);
	
		return {
			handlePrintClick,
			handleExcelClick,
			fetchData,
			defaultSAL6010_R01HeaderRow,
			defaultSAL6010_R01DetailRow,
			fetchOne,
			initContainerData,
			handleDeleteClick,
			handleCreateClick,
			handleSaveClick,
			handleOrderProcessClick,
		};
	}
